/**
 * BooklyAI AI Concierge — Chat panel mounted alongside the booking wizard.
 *
 * UX shape (Variant A — chat alongside wizard, not replacing it):
 * - Desktop (≥720px): a floating "Chat with concierge" button anchored to
 *   the viewport's bottom-right. Tap → 380×580 side panel slides in from
 *   the right. The wizard stays fully visible and usable behind it.
 * - Mobile (<720px): same launcher button. Tap → the panel expands to a
 *   full-height bottom sheet. Close returns to the wizard exactly where
 *   the customer left it.
 *
 * Markdown: Claude's replies often contain **bold**, bullet lists, and
 * paragraph breaks. We render a small, safe subset inline (no dependency)
 * so those don't leak as raw asterisks in the chat bubbles. HTML-injection
 * safe because we never dangerouslySetInnerHTML — text is split into React
 * fragments.
 *
 * Handoff: whenever the backend response includes a `handoff` object
 * ({service_id, date, time}), we invoke `onHandoff(...)` so the parent
 * (<Assistant/>) can pre-fill the wizard's state and jump to Step 3.
 * The chat panel auto-closes once the handoff CTA is pressed.
 *
 * Session persistence: a UUID v4 is generated on first mount and stored in
 * localStorage, so a page refresh restores the same server-side conversation.
 *
 * The panel silently hides itself when `config.conciergeEnabled === false`,
 * making feature-flag rollout a single-knob backend change.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { MessageCircle, Send, Sparkles, X } from "lucide-react";

// --- Tiny markdown renderer (dependency-free, XSS-safe by construction) ---
// Handles what Claude typically emits: **bold**, *italic*, `code`,
// bullet lists (- or *), and paragraph breaks. Never uses
// dangerouslySetInnerHTML — output is a tree of React fragments so any
// user-injected HTML stays as inert text.
function renderMarkdown(text) {
  if (!text) return null;
  const lines = String(text).split(/\r?\n/);
  const blocks = [];
  let listBuf = null;
  const flushList = () => {
    if (listBuf) {
      blocks.push({ type: "ul", items: listBuf });
      listBuf = null;
    }
  };
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, "");
    if (!line) { flushList(); continue; }
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    if (bullet) {
      (listBuf = listBuf || []).push(bullet[1]);
    } else {
      flushList();
      blocks.push({ type: "p", text: line });
    }
  }
  flushList();
  return blocks.map((b, i) => {
    if (b.type === "ul") {
      return (
        <ul key={i} className="concierge-md-ul">
          {b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
        </ul>
      );
    }
    return <p key={i} className="concierge-md-p">{renderInline(b.text)}</p>;
  });
}

// Inline: **bold**, *italic*, `code`. Split-and-replace pass; regex is
// non-greedy and anchored to preserve stray asterisks in prose.
function renderInline(str) {
  if (!str) return null;
  const parts = [];
  // Order matters: bold before italic so **x** doesn't get eaten by *x*.
  const re = /(\*\*[^*\n]+?\*\*|\*[^*\n]+?\*|`[^`\n]+?`)/g;
  let last = 0;
  let m;
  while ((m = re.exec(str)) !== null) {
    if (m.index > last) parts.push(str.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) parts.push(<strong key={parts.length}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("`")) parts.push(<code key={parts.length}>{tok.slice(1, -1)}</code>);
    else parts.push(<em key={parts.length}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
  }
  if (last < str.length) parts.push(str.slice(last));
  return parts.map((p, i) => typeof p === "string" ? <Fragment key={i}>{p}</Fragment> : p);
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_STORAGE_KEY = "bookly.concierge.session";

// UUID v4 without pulling in the `uuid` npm package — the browser's
// `crypto.randomUUID` is available in every modern env we target.
function newSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Fallback: RFC-4122 v4. `getRandomValues` must be bound to `crypto` or
  // it throws Illegal invocation; if the whole crypto obj is missing we
  // fall through to Math.random (best-effort in ancient envs).
  const b = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(b);
  } else {
    for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  }
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0"));
  return `${h.slice(0, 4).join("")}-${h.slice(4, 6).join("")}-${h.slice(6, 8).join("")}-${h.slice(8, 10).join("")}-${h.slice(10, 16).join("")}`;
}

export function ConciergeChat({ enabled, language, onHandoff }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role,content,handoff?}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [errored, setErrored] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Stable session id per browser
  const sessionId = useMemo(() => {
    if (typeof window === "undefined") return "";
    const existing = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const fresh = newSessionId();
    try { window.localStorage.setItem(SESSION_STORAGE_KEY, fresh); } catch {}
    return fresh;
  }, []);

  // Seed a welcome bubble the first time the customer opens the chat
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: t("concierge.welcome") }]);
      // Focus the input shortly after the panel animates in
      setTimeout(() => inputRef.current?.focus(), 250);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll to the newest bubble
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  // Lock body scroll while the mobile bottom sheet is open. Cleanup is
  // unconditional so a resize between mount and unmount can't leave the
  // body in a scroll-locked state (iter30 code review note).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    let locked = false;
    if (window.matchMedia("(max-width: 719px)").matches) {
      document.body.style.overflow = "hidden";
      locked = true;
    }
    return () => { if (locked) document.body.style.overflow = prev; };
  }, [open]);

  // ESC closes the panel (matches the Booking Detail Modal pattern)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!enabled) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setErrored(false);
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const r = await axios.post(`${API}/concierge/chat`, {
        session_id: sessionId,
        message: text,
        language: language || "en",
      });
      const reply = r.data?.reply || t("concierge.errorFallback");
      const handoff = r.data?.handoff || null;
      const isFallback = !!r.data?.fallback;
      setMessages((m) => [...m, { role: "assistant", content: reply, handoff, fallback: isFallback }]);
    } catch (err) {
      // 503 = feature flag off in prod → hide gracefully; anything else → error bubble
      if (err?.response?.status === 503) {
        setMessages((m) => [...m, { role: "assistant", content: t("concierge.offlineNotice"), fallback: true }]);
      } else {
        setErrored(true);
        setMessages((m) => [...m, { role: "assistant", content: t("concierge.errorFallback"), fallback: true }]);
      }
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const applyHandoff = (h) => {
    if (!h || !onHandoff) return;
    onHandoff({ serviceId: h.service_id, date: h.date, time: h.time });
    setMessages((m) => [...m, { role: "assistant", content: t("concierge.handoffApplied") }]);
    setOpen(false);
  };

  return (
    <>
      {/* Launcher button — always visible on customer surfaces when enabled */}
      {!open && (
        <button
          type="button"
          className="concierge-launcher"
          onClick={() => setOpen(true)}
          aria-label={t("concierge.openLabel")}
          data-testid="concierge-launcher"
        >
          <MessageCircle size={18} />
          <span>{t("concierge.openLabel")}</span>
        </button>
      )}

      {open && (
        <>
          <div className="concierge-backdrop" onClick={() => setOpen(false)} data-testid="concierge-backdrop" />
          <aside className="concierge-panel" data-testid="concierge-panel" role="dialog" aria-modal="true" aria-labelledby="concierge-title">
            <header className="concierge-head">
              <div className="concierge-head-info">
                <span className="concierge-avatar"><Sparkles size={16} /></span>
                <div>
                  <strong id="concierge-title" data-testid="concierge-title">{t("concierge.title")}</strong>
                  <small>{t("concierge.subtitle")}</small>
                </div>
              </div>
              <button type="button" className="concierge-close" onClick={() => setOpen(false)} aria-label={t("concierge.closeLabel")} data-testid="concierge-close">
                <X size={18} />
              </button>
            </header>
            <div className="concierge-thread" ref={scrollRef} data-testid="concierge-thread">
              {messages.map((m, i) => (
                <div key={i} className={`concierge-msg concierge-msg-${m.role}`} data-testid={`concierge-msg-${m.role}-${i}`}>
                  {m.role === "assistant" && <span className="concierge-bubble-avatar"><Sparkles size={12} /></span>}
                  <div className={`concierge-bubble ${m.fallback ? "concierge-bubble-fallback" : ""}`}>
                    {m.role === "assistant" ? renderMarkdown(m.content) : m.content}
                    {m.handoff && (
                      <button
                        type="button"
                        className="concierge-handoff-cta"
                        onClick={() => applyHandoff(m.handoff)}
                        data-testid={`concierge-handoff-cta-${i}`}
                      >
                        {t("concierge.handoffCta")}
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="concierge-msg concierge-msg-assistant" data-testid="concierge-typing">
                  <span className="concierge-bubble-avatar"><Sparkles size={12} /></span>
                  <div className="concierge-bubble concierge-typing">
                    <span /><span /><span />
                  </div>
                </div>
              )}
            </div>
            <div className="concierge-input-row">
              <textarea
                ref={inputRef}
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder={t("concierge.placeholder")}
                maxLength={500}
                data-testid="concierge-input"
                disabled={sending}
              />
              <button
                type="button"
                className="concierge-send"
                onClick={send}
                disabled={!input.trim() || sending}
                aria-label={t("concierge.sendLabel")}
                data-testid="concierge-send"
              >
                <Send size={16} />
              </button>
            </div>
            {errored && (
              <p className="concierge-error" data-testid="concierge-error">{t("concierge.errorFallback")}</p>
            )}
          </aside>
        </>
      )}
    </>
  );
}
